
import React, { useState, useEffect } from 'react';
import { CompanySettingsModal } from '@/components/CompanySettingsModal';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SendOfferModal from "@/components/SendOfferModal";
import ChatPanel from "@/components/ChatPanel";
import EmailPreview from "@/components/EmailPreview";
import SendEmailButton from "@/components/email-preview/SendEmailButton";
import { InsuranceAnalysisResult, Language } from '@/types/insurance';
import { Code, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import TemplateHistory from '@/components/TemplateHistory';

interface MobileBuilderLayoutProps {
  activePanel: "chat" | "preview";
  setActivePanel: (panel: "chat" | "preview") => void;
  view: "preview" | "code";
  setView: (view: "preview" | "code") => void;
  emailContent: string;
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  isStreaming: boolean;
  streamingCode: string;
  isInsuranceOffer: boolean;
  isInsuranceComparison: boolean;
  analysisResult: InsuranceAnalysisResult | null;
  detectedLanguage: Language;
  initialPrompt: string | null;
  handleEmailGenerated: (content: string, isOffer?: boolean, isComparison?: boolean, analysis?: any) => void;
  handleStreamingCodeUpdate: (html: string) => void;
  handleStreamingComplete: (finalHtml: string) => void;
  currentChatId: string | undefined;
  isOfferModalOpen: boolean;
  setIsOfferModalOpen: (open: boolean) => void;
  offerUrl: string;
  offerTitle: string;
  userEmail: string | undefined;
  containerRef: React.RefObject<HTMLDivElement>;
  triggerTemplateGeneration?: (concept: string) => Promise<void>;
  showHistory?: boolean;
  setShowHistory?: (show: boolean) => void;
  onTemplateSelect?: (content: string) => void;
}

const MobileBuilderLayout: React.FC<MobileBuilderLayoutProps> = ({
  activePanel,
  setActivePanel,
  view,
  setView,
  emailContent,
  isGenerating,
  setIsGenerating,
  isStreaming,
  streamingCode,
  isInsuranceOffer,
  isInsuranceComparison,
  analysisResult,
  detectedLanguage,
  initialPrompt,
  handleEmailGenerated,
  handleStreamingCodeUpdate,
  handleStreamingComplete,
  currentChatId,
  isOfferModalOpen,
  setIsOfferModalOpen,
  offerUrl,
  offerTitle,
  userEmail,
  containerRef,
  triggerTemplateGeneration,
  showHistory,
  setShowHistory,
  onTemplateSelect
}) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(emailContent);
      setCopied(true);
      toast({
        title: "Code kopiert!",
        description: "Der Code wurde in die Zwischenablage kopiert.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Fehler beim Kopieren",
        description: "Der Code konnte nicht kopiert werden.",
        variant: "destructive",
      });
    }
  };

  // Listen for panel change events
  useEffect(() => {
    const handleShowPreviewPanel = () => {
      setActivePanel("preview");
    };
    
    window.addEventListener('show-preview-panel', handleShowPreviewPanel);
    
    return () => {
      window.removeEventListener('show-preview-panel', handleShowPreviewPanel);
    };
  }, [setActivePanel]);

  return (
    <div className="h-full w-full overflow-hidden bg-gradient-to-br from-gray-950 via-gray-950 to-gray-950/95 backdrop-blur-sm" ref={containerRef}>
      <CompanySettingsModal />
      
      <div className="h-full w-full pt-14">
        <div className="absolute top-0 left-0 right-0 z-10 bg-black p-2 border-b border-gray-800/30 shadow-lg">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-none p-1 flex shadow-inner border border-gray-800">
            <button
              className={`flex-1 px-8 py-2 transition-all duration-300 ${
                activePanel === "chat" 
                  ? "bg-jade text-white" 
                  : "text-gray-400 hover:text-gray-200"
              } rounded-none`}
              onClick={() => setActivePanel("chat")}
            >
              Chat
            </button>
            <button
              className={`flex-1 px-8 py-2 transition-all duration-300 ${
                activePanel === "preview" 
                  ? "bg-jade text-white" 
                  : "text-gray-400 hover:text-gray-200"
              } rounded-none`}
              onClick={() => setActivePanel("preview")}
            >
              Vorschau
            </button>
          </div>
          
          {activePanel === "preview" && (
            <div className="mt-2 flex justify-end">
              <SendEmailButton 
                emailContent={emailContent} 
                userEmail={userEmail} 
                currentView={view}
              />
            </div>
          )}
        </div>
        
        {activePanel === "chat" ? (
          <div className="h-full overflow-hidden">
            <ChatPanel
              onEmailGenerated={handleEmailGenerated}
              onStreamingCodeUpdate={handleStreamingCodeUpdate}
              onStreamingComplete={handleStreamingComplete}
              initialPrompt={initialPrompt}
              chatId={currentChatId}
              key={currentChatId || 'new-chat'}
              isBuilderPage={true}
              setIsGenerating={setIsGenerating}
              triggerTemplateGeneration={triggerTemplateGeneration}
            />
          </div>
        ) : showHistory ? (
          <div className="h-full overflow-hidden">
            {onTemplateSelect && <TemplateHistory onSelect={onTemplateSelect} />}
          </div>
        ) : (
          <div className="h-full overflow-hidden">
            <EmailPreview
              view={view}
              onViewChange={setView}
              emailContent={emailContent}
              isGenerating={isGenerating}
              isStreaming={isStreaming}
              streamingCode={streamingCode}
              isInsuranceOffer={isInsuranceOffer}
              isInsuranceComparison={isInsuranceComparison}
              analysisResult={analysisResult}
              language={detectedLanguage}
              initialPrompt={initialPrompt || undefined}
              showViewToggle={false}
            />
          </div>
        )}
      </div>
      
      <Dialog open={isOfferModalOpen} onOpenChange={setIsOfferModalOpen}>
        <DialogContent className="bg-gradient-to-br from-gray-950 to-gray-950/95 border border-gray-800/50 text-white sm:max-w-md backdrop-blur-lg max-w-[90vw] mx-auto">
          <SendOfferModal
            offerUrl={offerUrl}
            offerTitle={offerTitle}
            onClose={() => setIsOfferModalOpen(false)}
            userEmail={userEmail}
            isOpen={isOfferModalOpen}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileBuilderLayout;
